import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useList, useOne } from "@refinedev/core";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { FileUpload } from "@/components/forms/FileUpload";

export function NuevoPagoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [periodoId, setPeriodoId] = useState(searchParams.get("periodo_id") ?? "");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [bancoOrigen, setBancoOrigen] = useState("");
  const [bancoDestino, setBancoDestino] = useState("");
  const [tipoCuenta, setTipoCuenta] = useState<"ahorros" | "corriente">("ahorros");
  const [cuenta, setCuenta] = useState("");
  const [titular, setTitular] = useState("");
  const [referencia, setReferencia] = useState("");
  const [notas, setNotas] = useState("");
  const [comprobante, setComprobante] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);

  const { data: periodos } = useList({
    resource: "periodos_cobro",
    filters: [{ field: "estado", operator: "eq", value: "liquidado" }],
    meta: { select: "*, tiendas(nombre, banco_default, cuenta_banco_default, titular_cuenta_default, tipo_cuenta_default)" },
    pagination: { mode: "off" },
  });

  const { data: periodoSel } = useOne({ resource: "periodos_cobro", id: periodoId || undefined });

  // Autocompletar destino al cambiar periodo (corrige bug previo: usaba useState).
  useEffect(() => {
    if (!periodoSel?.data) return;
    const tienda = periodoSel.data.tiendas as
      | {
          banco_default?: string;
          cuenta_banco_default?: string;
          titular_cuenta_default?: string;
          tipo_cuenta_default?: string;
        }
      | undefined;
    if (!tienda) return;
    setBancoDestino((b) => b || tienda.banco_default || "");
    setCuenta((c) => c || tienda.cuenta_banco_default || "");
    setTitular((t) => t || tienda.titular_cuenta_default || "");
    if (tienda.tipo_cuenta_default) {
      setTipoCuenta(tienda.tipo_cuenta_default as "ahorros" | "corriente");
    }
  }, [periodoSel?.data]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!comprobante) {
      toast.error("Adjunta el comprobante de la transferencia");
      return;
    }
    if (!user) return;

    setSubiendo(true);
    try {
      // 1. Subir comprobante a Supabase Storage
      const ext = comprobante.name.split(".").pop();
      const path = `comprobantes/${periodoId}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("documentos")
        .upload(path, comprobante);
      if (upErr) throw upErr;

      // 2. Crear registro de pago
      const valor = periodoSel?.data?.valor_final_transferencia ?? 0;
      const { error: insErr } = await supabase.from("pagos_tienda").insert({
        periodo_cobro_id: periodoId,
        fecha_transferencia: fecha,
        banco_origen: bancoOrigen || null,
        banco_destino: bancoDestino,
        tipo_cuenta_destino: tipoCuenta,
        cuenta_destino: cuenta,
        titular_destino: titular,
        valor_transferido: valor,
        numero_referencia_banco: referencia || null,
        comprobante_url: path,
        notas: notas || null,
        registrado_por: user.id,
      });
      if (insErr) throw insErr;

      // 3. Marcar periodo como pagado
      const { error: upPerErr } = await supabase
        .from("periodos_cobro")
        .update({ estado: "pagado", pagado_por: user.id, pagado_en: new Date().toISOString() })
        .eq("id", periodoId);
      if (upPerErr) throw upPerErr;

      // 4. Notificar a operadores de la tienda
      const tiendaId = periodoSel?.data?.tienda_id;
      if (tiendaId) {
        const { data: operadores } = await supabase
          .from("perfiles")
          .select("id")
          .eq("tienda_id", tiendaId)
          .eq("rol", "operador_tienda");
        if (operadores?.length) {
          await supabase.from("notificaciones").insert(
            operadores.map((o) => ({
              destinatario_id: o.id,
              tipo: "pago_realizado",
              titulo: "Transferencia realizada",
              mensaje: referencia ? `Ref: ${referencia}` : "Tu corte fue pagado",
              link: `/cuentas-cobro/${periodoId}`,
              icono: "check-circle",
            })),
          );
        }
      }

      toast.success("Pago registrado y tienda notificada");
      navigate("/pagos");
    } catch (err) {
      toast.error("Error", { description: (err as Error).message });
    } finally {
      setSubiendo(false);
    }
  }

  const hayPeriodosLiquidados = (periodos?.data?.length ?? 0) > 0;

  if (periodos?.data && !hayPeriodosLiquidados) {
    return (
      <div className="max-w-2xl space-y-4">
        <PageHeader title="Registrar pago" />
        <div className="card p-6 space-y-3 border-l-4 border-warning">
          <div className="flex items-start gap-3">
            <Info className="size-5 text-warning shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h3 className="font-semibold">Aún no hay cuentas de cobro listas para pagar</h3>
              <p className="text-sm text-fg-secondary">
                Para que aparezca una cuenta aquí, primero hay que liquidarla. El flujo completo es:
              </p>
              <ol className="text-sm text-fg-secondary list-decimal pl-5 space-y-1">
                <li>
                  La tienda <strong>cierra su corte</strong> en{" "}
                  <Link to="/cortes" className="text-primary underline">/cortes</Link>.
                </li>
                <li>
                  El admin abre la cuenta de cobro y la <strong>liquida</strong> en{" "}
                  <Link to="/cuentas-cobro" className="text-primary underline">/cuentas-cobro</Link>{" "}
                  (calcula bruto, 4x1000, fee y valor a transferir).
                </li>
                <li>
                  Una vez liquidada, vuelve aquí y registra el pago con su comprobante.
                </li>
              </ol>
              <p className="text-xs text-fg-muted pt-1">
                Si una tienda no ha cerrado corte aún, también puedes hacerlo tú desde{" "}
                <Link to="/cortes" className="text-primary underline">/cortes</Link>.
              </p>
            </div>
          </div>
        </div>
        <button onClick={() => navigate("/cuentas-cobro")} className="btn-primary">
          Ir a cuentas de cobro
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <PageHeader title="Registrar pago" />
      <form onSubmit={submit} className="card p-6 space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium">Cuenta de cobro a pagar *</label>
          <select
            value={periodoId}
            onChange={(e) => setPeriodoId(e.target.value)}
            required
            className="input"
          >
            <option value="">Selecciona...</option>
            {periodos?.data.map((p) => (
              <option key={p.id} value={p.id}>
                #{p.numero_correlativo as number} · {(p.tiendas as { nombre: string })?.nombre}
              </option>
            ))}
          </select>
          {periodoSel?.data && (
            <p className="text-sm text-fg-muted mt-1">
              Valor a transferir:{" "}
              <MoneyDisplay
                value={periodoSel.data.valor_final_transferencia as number}
                className="font-medium"
              />
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">Fecha transferencia *</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              required
              className="input"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Banco origen</label>
            <input value={bancoOrigen} onChange={(e) => setBancoOrigen(e.target.value)} className="input" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Banco destino *</label>
            <input value={bancoDestino} onChange={(e) => setBancoDestino(e.target.value)} required className="input" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Tipo cuenta *</label>
            <select
              value={tipoCuenta}
              onChange={(e) => setTipoCuenta(e.target.value as "ahorros" | "corriente")}
              className="input"
            >
              <option value="ahorros">Ahorros</option>
              <option value="corriente">Corriente</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">N° cuenta *</label>
            <input value={cuenta} onChange={(e) => setCuenta(e.target.value)} required className="input" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Titular *</label>
            <input value={titular} onChange={(e) => setTitular(e.target.value)} required className="input" />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Referencia bancaria</label>
          <input value={referencia} onChange={(e) => setReferencia(e.target.value)} className="input" />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Comprobante *</label>
          <FileUpload
            value={comprobante}
            onChange={setComprobante}
            accept="image/*,application/pdf"
            hint="PDF o imagen, máx 10MB"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Notas</label>
          <textarea value={notas} onChange={(e) => setNotas(e.target.value)} className="input min-h-20" />
        </div>

        <footer className="flex gap-2 pt-2">
          <button type="submit" disabled={subiendo || !comprobante} className="btn-primary">
            {subiendo ? "Subiendo..." : "Confirmar pago"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancelar
          </button>
        </footer>
      </form>
    </div>
  );
}
